import React, { useEffect, useMemo, useState } from "react";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "../components/ui/button";
import { Icons } from "../components/ui/icons";
import TabButton from "../components/tab-button";
import TabBar from "../components/tab-bar";
import PageShell from "../components/page-shell";
import FilterField from "../components/mobile/filter-field";
import { DesktopOnly } from "../components/responsive";
import SelectInput from "../components/select-input";
import DataTable, { TableRowData } from "../components/data-table";
import { warehousesTableColumns } from "../lib/table-columns";
import FormDrawer from "../components/form-drawer";
import { warehouseFormFields } from "../lib/form-fields";
import SearchInput from "../components/search-input";
import { useNavigate } from "react-router-dom";
import { Edit } from "lucide-react";
import BarGraph from "../components/bar-graph";
import HorizontalBarGraph from "../components/horizontal-bar-graph";
import {
  getWarehouses,
  getWarehouseStatistics,
  createWarehouse,
  updateWarehouse,
  CreateWarehousePayload,
  WarehouseStatistics,
  Warehouse,
  User,
} from "../lib/api";
import { RefreshToolbarButton } from "../components/refresh-toolbar-button";
import { toast } from "sonner";
import { useUser } from "../hooks/use-user";
import { getBackendErrorMessage } from "../lib/utils";

function userHasAssignedLocation(user: User): boolean {
  const n = (v: string | null | undefined) => (v ?? "").trim();
  return Boolean(
    n(user.country) ||
    n(user.region) ||
    n(user.district) ||
    n(user.ward) ||
    n(user.street),
  );
}

function warehouseMatchesUserLocation(w: Warehouse, user: User): boolean {
  if (user.role === "ADMIN") return true;
  const norm = (v: string | null | undefined) => (v ?? "").trim().toLowerCase();
  const uCountry = norm(user.country);
  const uRegion = norm(user.region);
  const uDistrict = norm(user.district);
  const uWard = norm(user.ward);
  const uStreet = norm(user.street);
  if (!uCountry && !uRegion && !uDistrict && !uWard && !uStreet) return true;
  if (uCountry && norm(w.country) !== uCountry) return false;
  if (uRegion && norm(w.region) !== uRegion) return false;
  if (uDistrict && norm(w.district) !== uDistrict) return false;
  if (uWard && norm(w.ward) !== uWard) return false;
  if (uStreet && norm(w.street) !== uStreet) return false;
  return true;
}

function buildScopedWarehouseStatistics(warehouses: Warehouse[]): WarehouseStatistics {
  const active = warehouses.filter((w) => w.status === "ACTIVE").length;
  const inactive = warehouses.filter((w) => w.status === "INACTIVE").length;
  const breakdown = [
    { status: "ACTIVE", count: active },
    { status: "INACTIVE", count: inactive },
  ];
  const byRegionMap = new Map<string, number>();
  for (const w of warehouses) {
    byRegionMap.set(w.region, (byRegionMap.get(w.region) || 0) + 1);
  }
  const byRegion = Array.from(byRegionMap.entries())
    .map(([region, count]) => ({ region, count }))
    .sort((a, b) => b.count - a.count);

  const warehousesChart = warehouses.map((w) => ({
    warehouseId: w.id,
    warehouseName: w.name,
    status: w.status,
    region: w.region,
    itemCount: 0,
    totals: {
      available: w.totalAvailableWeight ?? 0,
      damaged: w.totalDamagedWeight ?? 0,
      rented: w.totalRentedWeight ?? 0,
      reserved: w.totalReservedWeight ?? 0,
      lost: w.totalLostWeight ?? 0,
      sold: w.totalSoldWeight ?? 0,
      total: w.totalWeight ?? 0,
      value: w.totalValue ?? 0,
    },
  }));

  let available = 0;
  let damaged = 0;
  let rented = 0;
  let reserved = 0;
  let lost = 0;
  let sold = 0;
  let totalW = 0;
  let totalValue = 0;
  for (const row of warehousesChart) {
    available += row.totals.available;
    damaged += row.totals.damaged;
    rented += row.totals.rented;
    reserved += row.totals.reserved;
    lost += row.totals.lost;
    sold += row.totals.sold;
    totalW += row.totals.total;
    totalValue += row.totals.value;
  }
  const n = warehouses.length || 1;

  return {
    totalWarehouses: warehouses.length,
    byStatus: { active, inactive, breakdown },
    totals: {
      inventoryItems: 0,
      available,
      damaged,
      rented,
      reserved,
      lost,
      sold,
      total: totalW,
      value: Math.round(totalValue * 100) / 100,
    },
    averages: {
      itemsPerWarehouse: 0,
      availablePerWarehouse: available / n,
      rentedPerWarehouse: rented / n,
    },
    byRegion,
    warehouses: warehousesChart,
  };
}

const Warehouses: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  const [currentTab, setCurrentTab] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [selectedRegion, setSelectedRegion] = useState<string>("all");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [formValues, setFormValues] = useState<{
    [key: string]: unknown;
  } | null>(null);

  const queryClient = useQueryClient();

  const isLocationScoped = Boolean(
    user && user.role !== "ADMIN" && userHasAssignedLocation(user),
  );

  const mapWarehouseToRow = (w: Warehouse) => ({
    id: w.id,
    name: w.name,
    location: `${w.ward}, ${w.district}, ${w.region}`,
    point_of_contact: `${w.pointOfContactName}, ${w.pointOfContactPhone}`,
    available_weight: w.totalAvailableWeight || 0,
    rented_weight: w.totalRentedWeight || 0,
    damaged_weight: w.totalDamagedWeight || 0,
    reserved_weight: w.totalReservedWeight || 0,
    lost_weight: w.totalLostWeight || 0,
    sold_weight: w.totalSoldWeight || 0,
    Total_weight: w.totalWeight || 0,
    total_value: Number(w.totalValue || 0).toLocaleString(),
    status: w.status,
    raw_data: w,
  });

  const statusParam =
    currentTab === "Active"
      ? "ACTIVE"
      : currentTab === "Inactive"
        ? "INACTIVE"
        : undefined;

  // If location scoped: we must fetch a bigger slice and paginate client-side.
  const scopedWarehousesQuery = useQuery({
    queryKey: ["warehouses", "scoped", { search: searchTerm, status: statusParam }],
    queryFn: async () => getWarehouses(1, 1000, searchTerm, statusParam),
    enabled: Boolean(isLocationScoped && user),
    placeholderData: keepPreviousData,
  });

  // If not location scoped: fetch server paginated plus server stats.
  const warehousesQuery = useQuery({
    queryKey: ["warehouses", { page: currentPage, limit: pageSize, search: searchTerm, status: statusParam }],
    queryFn: async () => getWarehouses(currentPage, pageSize, searchTerm, statusParam),
    enabled: !isLocationScoped,
    placeholderData: keepPreviousData,
  });

  const statisticsQuery = useQuery<WarehouseStatistics>({
    queryKey: ["warehouses", "statistics"],
    queryFn: async () => getWarehouseStatistics(),
    enabled: !isLocationScoped,
  });

  const locationFilteredWarehouses = useMemo(() => {
    if (!isLocationScoped || !user) return [];
    const data = scopedWarehousesQuery.data?.data ?? [];
    return data.filter((w) => warehouseMatchesUserLocation(w, user));
  }, [isLocationScoped, user, scopedWarehousesQuery.data]);

  const regionFilteredWarehouses = useMemo(() => {
    if (!isLocationScoped) return [];
    if (!selectedRegion || selectedRegion === "all") return locationFilteredWarehouses;
    return locationFilteredWarehouses.filter((w) => w.region === selectedRegion);
  }, [isLocationScoped, selectedRegion, locationFilteredWarehouses]);

  const scopedTotalItems = regionFilteredWarehouses.length;
  const scopedTotalPages = Math.max(1, Math.ceil(scopedTotalItems / pageSize));
  const scopedSafePage = Math.min(Math.max(1, currentPage), scopedTotalPages);
  const scopedSliceStart = (scopedSafePage - 1) * pageSize;
  const scopedPageSlice = regionFilteredWarehouses.slice(scopedSliceStart, scopedSliceStart + pageSize);

  useEffect(() => {
    if (!isLocationScoped) return;
    if (scopedSafePage !== currentPage) setCurrentPage(scopedSafePage);
  }, [isLocationScoped, scopedSafePage, currentPage]);

  const warehouses: TableRowData[] = useMemo(() => {
    if (isLocationScoped) return scopedPageSlice.map(mapWarehouseToRow);
    const data = warehousesQuery.data?.data ?? [];
    const mapped = data.map(mapWarehouseToRow);
    if (!selectedRegion || selectedRegion === "all") return mapped;
    return mapped.filter((w) => (w as { raw_data: Warehouse }).raw_data.region === selectedRegion);
  }, [isLocationScoped, scopedPageSlice, warehousesQuery.data, selectedRegion]);

  const totalItems = isLocationScoped ? scopedTotalItems : (warehousesQuery.data?.meta.total ?? 0);
  const totalPages = isLocationScoped ? scopedTotalPages : (warehousesQuery.data?.meta.totalPages ?? 1);

  const statistics: WarehouseStatistics | null = useMemo(() => {
    if (isLocationScoped) return buildScopedWarehouseStatistics(locationFilteredWarehouses);
    return statisticsQuery.data ?? null;
  }, [isLocationScoped, locationFilteredWarehouses, statisticsQuery.data]);

  const initialLoading =
    (isLocationScoped ? scopedWarehousesQuery.isLoading : (warehousesQuery.isLoading || statisticsQuery.isLoading));

  // no-op: queries react to deps

  // Reset pagination on tab or region changes (search handled separately).
  // Suppress full-page loading so filter changes don't refresh the whole page.
  useEffect(() => {
    setCurrentPage(1);
  }, [currentTab, selectedRegion]);

  // Debounce raw search input and apply to `searchTerm` after a short delay
  useEffect(() => {
    const handler = setTimeout(() => setSearchTerm(searchInput), 400);
    return () => clearTimeout(handler);
  }, [searchInput]);

  // When searchTerm changes (debounced above), perform a search without showing
  // the page loading state. If we're not already on page 1, set the ref and
  // reset page to 1 so the page effect will run suppressed; otherwise fetch
  // directly suppressed.
  useEffect(() => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [searchTerm]);

  const actions = [
    {
      label: "View details",
      icon: Icons.Preview,
      onClick: (row: TableRowData) => {
        const raw = (row as any).raw_data;
        navigate("/inventory", {
          state: { warehouse: row?.name ?? "", id: raw.id },
        });
      },
    },
    {
      label: "Edit warehouse",
      icon: Edit,
      onClick: (row: TableRowData) => {
        const raw = (row as any).raw_data;
        setEditingId(raw.id);

        setFormValues({
          name: raw.name,
          street: raw.street,
          location: `${raw.ward ? raw.ward + ", " : ""}${raw.district}, ${raw.region}`,
          contact_name: raw.pointOfContactName,
          contact_phone_number: raw.pointOfContactPhone,
          status: raw.status,
        });
      },
    },
  ];

  const handleFormSubmit = async () => {
    if (!formValues) return;

    setIsSubmitting(true);

    // Parse location "Ward, District, Region"
    const locationStr = (formValues.location as string) || "";
    const locationParts = locationStr.split(",").map((s) => s.trim());
    const region =
      locationParts.length > 0 ? locationParts[locationParts.length - 1] : "";
    const district =
      locationParts.length > 1 ? locationParts[locationParts.length - 2] : "";
    const ward =
      locationParts.length > 2 ? locationParts[locationParts.length - 3] : "";

    // Construct payload
    const payload: CreateWarehousePayload = {
      name: formValues.name as string,
      street: (formValues.street as string) || "",
      ward: ward || "",
      district: district || "",
      region: region || "",
      country: "Tanzania",
      pointOfContactName: formValues.contact_name as string,
      pointOfContactPhone: formValues.contact_phone_number as string,
      status: (formValues.status as "ACTIVE" | "INACTIVE") || "ACTIVE",
    };

    try {
      if (editingId) {
        await updateWarehouse(editingId, payload);
      } else {
        await createWarehouse(payload);
      }
      setFormValues(null);
      setEditingId(null);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["warehouses"] }),
        queryClient.invalidateQueries({ queryKey: ["warehouses", "statistics"] }),
      ]);
    } catch (error) {
      toast.error(getBackendErrorMessage(error, "Failed to save warehouse"));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Calculate percentages for BarGraph (by region)
  const totalRegionsCount =
    statistics?.byRegion.reduce((acc, curr) => acc + curr.count, 0) || 1;
  const regionMetrics =
    (statistics?.byRegion.map((r) => ({
      label: r.region,
      count: r.count,
      percentage: Math.round((r.count / totalRegionsCount) * 100),
      avgDays: 0,
    })) || []).sort((a, b) => b.count - a.count);

  // Data for HorizontalBarGraph (warehouse inventory breakdown)
  const warehouseInventoryData =
    statistics?.warehouses.slice(0, 5).map((w) => ({
      category: w.warehouseName,
      gap: 0.2,
      values: [
        {
          label: "Available",
          value: w.totals.available,
          fillColor: "#bce3ff",
        },
        {
          label: "Rented",
          value: w.totals.rented,
          fillColor: "#e9d5ff",
        },
        {
          label: "Damaged",
          value: w.totals.damaged,
          fillColor: "#ffd6a8",
        },
        {
          label: "Lost",
          value: w.totals.lost || 0,
          fillColor: "#d4d4d8",
        },
        {
          label: "Sold",
          value: w.totals.sold || 0,
          fillColor: "#a7f3d0",
        },
      ],
    })) || [];

  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ["warehouses"] }),
      queryClient.invalidateQueries({ queryKey: ["warehouses", "statistics"] }),
    ]);

  const openNewWarehouse = () => setFormValues({});

  const regionSelect = (
    <SelectInput
      value={selectedRegion}
      options={[
        { label: "All Regions", value: "all" },
        ...(statistics?.byRegion.map((r) => ({
          label: r.region,
          value: r.region,
        })) || []),
      ]}
      placeholder={"Region"}
      onChange={(value) => {
        setSelectedRegion(value);
      }}
    />
  );

  return (
    <>
      <FormDrawer
        title={editingId ? "Edit Warehouse" : "Add New Warehouse"}
        buttonText={editingId ? "Update Warehouse" : "Add Warehouse"}
        formValues={formValues}
        setFormValues={setFormValues}
        fields={warehouseFormFields}
        handleSubmit={handleFormSubmit}
        isSubmitting={isSubmitting}
        onClose={() => {
          setFormValues(null);
          setEditingId(null);
        }}
      />
      <PageShell
        title="Warehouses"
        description="Manage warehouses which stores items on the inventories."
        loading={initialLoading}
        toolbar={
          <>
            <RefreshToolbarButton
              isRefreshing={
                scopedWarehousesQuery.isFetching ||
                warehousesQuery.isFetching ||
                statisticsQuery.isFetching
              }
              onRefresh={refresh}
            />
            <Button variant="default" className="h-9" onClick={openNewWarehouse}>
              <Icons.Plus className="h-4 w-4 mr-2" />
              New Warehouse
            </Button>
          </>
        }
        stats={[
          {
            label: "Total Warehouses",
            value: statistics?.totalWarehouses.toString() || "0",
            icon: <Icons.Building className="w-5 h-5 text-primary-600" />,
          },
          {
            label: "Active",
            value: statistics?.byStatus.active.toString() || "0",
            icon: <Icons.Building className="w-5 h-5 text-green-600" />,
          },
          {
            label: "Not Active",
            value: statistics?.byStatus.inactive.toString() || "0",
            icon: <Icons.Building className="w-5 h-5 text-red-600" />,
          },
          {
            label: "Total Value",
            value: `TZS ${(statistics?.totals.value || 0).toLocaleString()}`,
            icon: <Icons.Inventory className="w-5 h-5 text-primary-600" />,
          },
        ]}
        statsColumns={4}
        insights={
          <div className="grid grid-cols-1 lg:grid-cols-[1fr,400px] gap-4 mt-6">
            <HorizontalBarGraph
              graphTitle="Warehouse Inventory Summary"
              data={warehouseInventoryData.slice(0, 3)}
            />
            <BarGraph graphTitle="Warehouses by Region" metrics={regionMetrics.slice(0, 3)} />
          </div>
        }
        mobile={{
          onRefresh: refresh,
          primaryAction: { label: "New Warehouse", icon: Icons.Plus, onClick: openNewWarehouse },
          filters: <FilterField label="Region">{regionSelect}</FilterField>,
          activeFilterCount: selectedRegion !== "all" ? 1 : 0,
          onResetFilters: () => setSelectedRegion("all"),
        }}
      >
        <div className="flex flex-wrap items-center justify-between gap-2">
          <TabBar>
            {[
              { label: "All", count: statistics?.totalWarehouses || 0 },
              { label: "Active", count: statistics?.byStatus.active || 0 },
              { label: "Inactive", count: statistics?.byStatus.inactive || 0 },
            ].map((tab) => (
              <TabButton
                key={tab.label}
                label={tab.label}
                count={tab.count}
                active={currentTab === tab.label}
                onClick={() => setCurrentTab(tab.label)}
              />
            ))}
          </TabBar>
          <DesktopOnly>{regionSelect}</DesktopOnly>
        </div>
        <SearchInput
          searchTerm={searchInput}
          onSearchChange={(value) => setSearchInput(value)}
          placeholder="Search warehouses..."
        />
        <DataTable
          columns={warehousesTableColumns}
          data={warehouses}
          actions={actions}
          loading={isLocationScoped ? scopedWarehousesQuery.isFetching : warehousesQuery.isFetching}
          pagination={{
            page: currentPage,
            pageSize,
            totalItems,
            totalPages,
            onPageChange: (page) => {
              setCurrentPage(page);
            },
            onPageSizeChange: (size) => {
              setPageSize(size);
            },
          }}
        />
      </PageShell>
    </>
  );
};

export default Warehouses;
